import { computed, onActivated, onBeforeUnmount, onMounted, ref } from 'vue'
import { recentDramaId } from './dramaBooks'
import { dramaVersion } from './dramaMedia'
import { isVideoBridge } from './videoBridge'
import { api } from '@/api'

// 本轮双书展示的唯一配置入口。正式上线只填写 production；留空即不启用。
// 删除实验时移除本文件及三个页面的接入；不影响播放器、存档或购买权益。
export const DRAMA_BOOKS = {
  testing: { A: 'ybkhma4lLJQujR1O', B: '0CH4MVqeCjOXRsCy' },
  production: { A: 'iuG1N4Wd0UaCl2ce', B: 'CKsq3Z0tY95m40dC' },
}
const mode = import.meta.env.MODE
export const pair = mode === 'testing' || mode === 'development'
  ? DRAMA_BOOKS.testing
  : DRAMA_BOOKS.production
export const experimentEnabled = !!(pair.A && pair.B)
const bookId = (item: any): string => item?.contentId || item?.id || ''
export const isExperimentBook = (item: any): boolean => experimentEnabled && [pair.A, pair.B].includes(bookId(item))

export function selectDrama(items: any[], recent: string | null, enabled = experimentEnabled): any {
  if (!enabled)
    return items.find(item => dramaVersion(item.video_entry?.oss_root) === 'A') ?? null
  const id = recent === pair.A || recent === pair.B ? recent : pair.A
  return items.find(item => bookId(item) === id) ?? null
}

// 只合并已加载的历史，不改原数组；只有一版历史时保留已有记录。
export function mergeDramaHistory(items: any[], selectedId: string): any[] {
  const variants = items.filter(isExperimentBook)
  if (variants.length < 2)
    return items
  const selected = variants.find(item => bookId(item) === selectedId) ?? variants[0]
  let inserted = false
  return items.flatMap((item) => {
    if (!isExperimentBook(item))
      return [item]
    if (inserted)
      return []
    inserted = true
    return [selected]
  })
}

// 替换书籍数据，仅保留选中书籍自己的 Banner 封面和视频。
export function replaceDramaBanners(items: any[], selected: any): any[] {
  let inserted = false
  return items.flatMap((item) => {
    if (!isExperimentBook(item))
      return [item]
    if (inserted || !selected)
      return []
    inserted = true
    const original = items.find(item => bookId(item) === bookId(selected))
    return [{
      ...selected,
      id: bookId(selected),
      contentCoverUrl: original?.contentCoverUrl || selected.coverUrl,
      bannerVideoUrl: original?.bannerVideoUrl || selected.bannerVideoUrl,
      landscapeCoverUrl: original?.landscapeCoverUrl || selected.landscapeCoverUrl,
    }]
  })
}

const books = ref<any[]>([])
const revision = ref(0)
const fetching = ref(false)
const error = ref(false)
let pending: Promise<void> | null = null
async function loadBooks() {
  if (pending)
    return pending
  fetching.value = true
  error.value = false
  pending = (async () => {
    try {
      const { data } = await api.get<any, { data: any[] }>('/interactive/book/video-bridge/list')
      books.value = (data ?? []).filter(isVideoBridge)
    }
    catch { error.value = true }
    finally {
      fetching.value = false
      pending = null
    }
  })()
  return pending
}

export function useDramaExperiment(preferRecent = false) {
  const refresh = () => {
    revision.value++
  }
  const selected = computed(() => {
    const localRevision = revision.value
    const recent = recentDramaId()
    const book = !experimentEnabled && preferRecent
      ? books.value.find(item => bookId(item) === recent) ?? selectDrama(books.value, recent)
      : selectDrama(books.value, recent)
    // 版本未变也更新集数，确保返回页面时重新读取本地存档。
    return book ? { ...book, localRevision } : null
  })
  // 页面从播放器返回或从后台恢复时重新读取；不重写最近播放记录。
  const visible = () => {
    if (document.visibilityState === 'visible')
      refresh()
  }
  onMounted(() => {
    refresh()
    if (!books.value.length)
      loadBooks()
    window.addEventListener('pageshow', refresh)
    document.addEventListener('visibilitychange', visible)
  })
  onActivated(refresh)
  onBeforeUnmount(() => {
    window.removeEventListener('pageshow', refresh)
    document.removeEventListener('visibilitychange', visible)
  })
  return { books, selected, fetching, error, reload: loadBooks }
}
